import React, { useState, useEffect } from 'react';

import Grid from '@mui/material/Grid2';

import { 
  Campaign, 
  GameCard, 
  CampaignDeck,
  Character,
  CharacterDrawnCard,
} from "@dndModels";
import { DisablableOption, ModelBase } from "@models";

import API from "@utilities/smart_api";
import CommonUtilities from "@utilities/common_utilities";

import ButtonBox from "@components/input/buttons/ButtonBox";
import StringBox from "@components/input/strings/StringBox";
import AddButton from "@components/input/buttons/icon_buttons/AddButton";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import DisplayObjects from "@dndComponents/display/DisplayObjects";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import DisplayStringWithReplacements from "@dndComponents/display/DisplayStringWithReplacements";

interface Props { 
  campaign: Campaign;
  campaign_deck: CampaignDeck;
  characters: Character[];
  character_drawn_cards: CharacterDrawnCard[];
  cards: GameCard[];
}

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

export default function CardsDrawnByCharactersInput({ 
  campaign, 
  campaign_deck,
  characters,
  character_drawn_cards,
  cards,
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [ping, setPing] = useState(false);
  const [character_options, setCharacterOptions] = useState<DisablableOption[]>([]);
  const [card_options, setCardOptions] = useState<DisablableOption[]>([]);
  const [drawn_cards_for_character, setDrawnCardsForCharacter] = useState<{ [char_id: string]: CharacterDrawnCard[] }>({});
  const [expanded_card, setExpandedCard] = useState<CharacterDrawnCard | null>(null);
  const [details, setDetails] = useState("");
  const [character_id, setCharacterId] = useState("");
  const [card_id, setCardId] = useState("");
  const [open, setOpen] = useState(false);
  const [change_time, setChangeTime] = useState(common_util.get_time_number());
  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      setCharacterOptions(characters.filter(o => o.active && !o.is_archived).sort((a, b) => a.name.localeCompare(b.name)).map(o => {
        const option: DisablableOption = {
          id: o._id,
          text: o.name,
          disabled: false,
        };
        return option;
      }));
      setCardOptions(cards.sort((a, b) => a.name.localeCompare(b.name)).map(o => {
        const option: DisablableOption = {
          id: o._id,
          text: o.name,
          disabled: false,
          image_url: o.image_url,
        };
        return option;
      }));
      const drawn_cards: { [char_id: string]: CharacterDrawnCard[] } = {};
      for (const char of characters) {
        const char_drawn_cards = character_drawn_cards.filter(o => o.char_id === char._id);
        drawn_cards[char._id] = char_drawn_cards;
      }
      setDrawnCardsForCharacter(drawn_cards);
    }
  }, [fetched, setFetched, characters, character_drawn_cards, cards]);

  const renderExpandedCard = (char_id: string) => {
    if (expanded_card && expanded_card.char_id === char_id) {
      const card_obj = cards.find(o => o._id === expanded_card.card_id);
      if (card_obj) {
        return (
          <>
            <Grid
              className='centered'
              size={{
                xs: 12
              }}>
              <img src={ card_obj.image_url } alt={ card_obj.name }
                className='centered'
                style={{
                  maxWidth: "400px",
                  maxHeight: "400px"
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12
              }}>
              <DisplayStringWithReplacements
                display_string={card_obj.description}
              />
            </Grid>
            <Grid
              size={{
                xs: 12
              }}>
              <StringBox
                name="Details"
                value={expanded_card.details}
                onBlur={ async (changed: string) => {
                  expanded_card.details = changed;
                  await api.upsertObject("character_drawn_card", expanded_card);
                  setPing(!ping);
                }}
              />
            </Grid>
          </>
        );
      }
    }
    return null;
  }

  const renderSelectedCard = () => {
    if (card_id !== "") {
      const card_obj = cards.find(o => o._id === card_id);
      if (card_obj) {
        return (
          <>
            <Grid
              className='centered'
              size={{
                xs: 12
              }}>
              <img src={ card_obj.image_url } alt={ card_obj.name }
                className='centered'
                style={{
                  maxWidth: "400px",
                  maxHeight: "400px"
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12
              }}>
              <DisplayStringWithReplacements
                display_string={card_obj.description}
              />
            </Grid>
          </>
        );
      }
    }
    return null;
  }

  const renderExtras = () => {
    return (
      <ModalOrDrawer
        open={ open }
        onClose={() => {
          setOpen(false);
        }}
      >
        <Grid container spacing={0} direction="row">
          <Grid
            className="header"
            size={{
              xs: 12
            }}>
            Mark Card as Drawn
          </Grid>
          <Grid
            size={{
              xs: 6
            }}>
            <SelectStringBox
              name="Character"
              value={character_id}
              options={character_options}
              onChange={(changed: string) => {
                setCharacterId(changed);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 6
            }}>
            <SelectStringBox
              name="Card"
              value={card_id}
              options={card_options}
              onChange={(changed: string) => {
                setCardId(changed);
              }}
            />  
          </Grid>
          { renderSelectedCard() }
          <Grid
            size={{
              xs: 12
            }}>
            <StringBox
              name="Details"
              value={details}
              change_time={change_time}
              multiline
              onChange={(changed: string) => {
                setDetails(changed);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <ButtonBox
              name="Save"
              disabled={ character_id === "" || card_id === "" }
              onClick={ async () => {
                const card = new CharacterDrawnCard();
                card.char_id = character_id;
                card.card_id = card_id;
                card.details = details;
                // card.campaign_deck_id = campaign_deck._id;
                // card.campaign_id = campaign._id;
                card.drawn_dt = common_util.get_time_number();
                const res = await api.upsertObject("character_drawn_card", card);
                if (res && res.body) {
                  card._id = res.body._id;
                  character_drawn_cards.push(card);
                  setDrawnCardsForCharacter(prev => ({ ...prev, [character_id]: [...prev[character_id], card] }));
                }
                setCardId("");
                setDetails("");
                setChangeTime(common_util.get_time_number());
              }}
            />
          </Grid>
        </Grid>
      </ModalOrDrawer>
    );
  }

  return (
    <Grid container spacing={0} direction="row">
      <Grid
        size={{
          xs: 12
        }}>
        <AddButton
          label="Add Drawn Card"
          onClick={() => {
            setOpen(true);
          }}
        />
      </Grid>
      { character_options.map(o => {
        return (
          <Grid
            key={o.id}
            size={{
              xs: 12
            }}>
            <Grid container spacing={0} direction="row">
              <Grid
                size={{
                  xs: 3
                }}>
                { o.text }
              </Grid>
              <Grid
                size={{
                  xs: 9
                }}>
                <DisplayObjects type="game_card" ids={drawn_cards_for_character[o.id].map(o => o.card_id)} 
                  onClick={(selected: ModelBase) => {
                    // console.log(selected);
                    if (selected instanceof GameCard) {
                      const drawn_card = drawn_cards_for_character[o.id].find(o => o.card_id === selected._id);
                      if (drawn_card) {
                        if (expanded_card && expanded_card._id === drawn_card._id) {
                          setExpandedCard(null);
                        } else {
                          setExpandedCard(drawn_card);
                        }
                      }
                    }
                  }} 
                />
              </Grid>
              { renderExpandedCard(o.id) }
            </Grid>
          </Grid>
        );
      })}
      { renderExtras()}
    </Grid>
  );
}
